import { Controller } from "@hotwired/stimulus"
import { logger } from "../logger"

// Handles feedback report generation for failed attachment validations.
// Triggers report generation and displays status using existing report_controller infrastructure.
export default class extends Controller {
  static targets = ["button"]
  static values = {
    sourceId: Number,  // FeedbackReportSource ID
    successTitle: String,
    successSubtitle: String
  }

  connect() {
    // Store original button text for reset
    if (this.hasButtonTarget) {
      this.originalButtonHTML = this.buttonTarget.innerHTML
    }
  }

  async generate(event) {
    event.preventDefault()

    if (!this.hasSourceIdValue) {
      logger.error("FeedbackReportController: No source ID provided")
      return
    }

    // Disable button and show loading state in button text
    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = true
      this.buttonTarget.innerHTML = '<i class="fas fa-spinner fa-spin me-2"></i>מייצר דוח שגיאות...'
    }

    try {
      const response = await fetch('/feedback_reports/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ source_id: this.sourceIdValue })
      })

      const data = await response.json()

      if (response.ok && data.report_cache_id) {
        logger.log("FeedbackReportController: Report generation started, cache ID:", data.report_cache_id)

        // Replace modal content with success message
        this.showSuccessMessage()

        // Show report status using existing infrastructure (same as data table export)
        this.showReportStatus(data.report_cache_id)

        // Dispatch event to notify parent controllers (e.g., attachment_validation_controller)
        this.dispatch('generated', { detail: { reportCacheId: data.report_cache_id } })
      } else {
        // Error response
        const errorMessage = data.error || 'שגיאה ביצירת הדוח'
        this.showError(errorMessage)
      }
    } catch (error) {
      logger.error("FeedbackReportController: Generate request failed:", error)
      this.showError('שגיאה בתקשורת עם השרת')
    }
  }

  showSuccessMessage() {
    // Find the modal body and replace its content with success message
    // Button may be in .modal-footer (sibling of .modal-body), so find via .modal-content
    const modalContent = this.element.closest('.modal-content')
    const modalBody = modalContent?.querySelector('.modal-body')
    if (modalBody) {
      const title = this.hasSuccessTitleValue ? this.successTitleValue : 'הדוח ירד בעוד מספר שניות.'
      const subtitle = this.hasSuccessSubtitleValue ? this.successSubtitleValue : 'ניתן לסגור את החלון.'
      modalBody.innerHTML = `
        <div class="text-center py-4">
          <i class="fas fa-check-circle text-success fa-3x mb-3"></i>
          <p class="fw-bold mb-2">${title}</p>
          <p class="text-muted">${subtitle}</p>
        </div>
      `
      // Hide the feedback button in footer since we're showing success
      if (this.hasButtonTarget) {
        this.buttonTarget.style.display = 'none'
      }
    }
  }

  showReportStatus(reportCacheId) {
    // Create or get report statuses container (same pattern as show.js.erb)
    let container = document.getElementById('reportStatuses')
    if (!container) {
      container = document.createElement('div')
      container.id = 'reportStatuses'
      container.className = 'sidebar-aware'

      // Apply sidebar collapsed class if sidebar is collapsed (same as show.js.erb)
      const sidebarController = document.body.querySelector('[data-controller*="sidebar"]')
      if (sidebarController) {
        const sidebarCollapsed = sidebarController.dataset.sidebarCollapsedValue === 'true'
        if (sidebarCollapsed) {
          container.classList.add('sidebar-collapsed')
        }
      } else {
        // Fallback: check main-content-wrapper for sidebar state
        const mainContent = document.querySelector('.main-content-wrapper')
        if (mainContent && mainContent.classList.contains('sidebar-collapsed')) {
          container.classList.add('sidebar-collapsed')
        }
      }

      document.body.appendChild(container)

      // Force a reflow to ensure CSS is applied before adding content
      container.offsetHeight
    }

    // Create status element with report_controller (same pattern as data_table_controller)
    const statusHTML = `
      <div class="report-status"
           id="reportStatus${reportCacheId}"
           data-controller="report"
           data-report-report-cache-id-value="${reportCacheId}">
        <div class="report-status-loader" data-report-target="status"></div>
        <span class="report-status-message" data-report-target="message">מכין דוח שגיאות...</span>
        <button class="report-status-close" data-action="click->report#close" data-report-target="close">×</button>
      </div>
    `
    container.insertAdjacentHTML('beforeend', statusHTML)

    // Show container with animation
    setTimeout(() => {
      container.classList.add('show')
    }, 10)

    // Store in sessionStorage for restoration (same as data_table_controller)
    try {
      const activeReports = JSON.parse(sessionStorage.getItem('activeReportCaches') || '[]')
      if (!activeReports.includes(reportCacheId)) {
        activeReports.push(reportCacheId)
        sessionStorage.setItem('activeReportCaches', JSON.stringify(activeReports))
      }
    } catch (e) {
      logger.warn("FeedbackReportController: Could not save to sessionStorage:", e)
    }

    // Hide the button area since report status is now showing
    if (this.hasButtonTarget) {
      this.buttonTarget.closest('.text-center')?.classList.add('d-none')
    }
  }

  showError(message) {
    // Re-enable button and show error in button text
    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = false
      this.buttonTarget.innerHTML = `<i class="fas fa-exclamation-triangle text-danger me-2"></i>${message}`

      // Reset to original after 3 seconds
      setTimeout(() => {
        if (this.hasButtonTarget) {
          this.buttonTarget.innerHTML = this.originalButtonHTML || '<i class="fas fa-file-excel me-2"></i>הורד דוח שגיאות מפורט'
        }
      }, 3000)
    }
  }
}
